/**
 * Backup codes management component.
 * Allows users to view and regenerate their backup/recovery codes.
 */

import { useState, useEffect, useCallback } from 'react';
import type { UiNodeInputAttributes } from '@ory/client';
import { kratos } from '../lib/kratos';
import LoadingSpinner from './LoadingSpinner';

interface BackupCodesProps {
  /** Current settings flow ID */
  flowId: string;
  /** Callback when codes are successfully regenerated */
  onRegenerate?: () => void;
}

export default function BackupCodes({ flowId, onRegenerate }: BackupCodesProps) {
  const [codes, setCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCodes, setShowCodes] = useState(false);
  const [hasLookupSecret, setHasLookupSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  const fetchBackupCodes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: flow } = await kratos.getSettingsFlow({ id: flowId });

      // Get CSRF token
      const csrfNode = flow.ui.nodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'csrf_token';
      });

      if (csrfNode) {
        const attrs = csrfNode.attributes as UiNodeInputAttributes;
        setCsrfToken(attrs.value as string);
      }

      // Find lookup_secret nodes
      const lookupNodes = flow.ui.nodes.filter(
        (node) => node.group === 'lookup_secret'
      );

      // Check if lookup_secret is already configured
      const revealNode = lookupNodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'lookup_secret_reveal';
      });

      const regenerateNode = lookupNodes.find((node) => {
        const attrs = node.attributes as UiNodeInputAttributes;
        return attrs.name === 'lookup_secret_regenerate';
      });

      // Check for existing codes in text nodes
      const codesNode = lookupNodes.find((node) => {
        return node.type === 'text' && node.attributes && 'text' in node.attributes;
      });

      if (codesNode && codesNode.attributes && 'text' in codesNode.attributes) {
        // Codes are visible
        const textAttr = codesNode.attributes as { text: { text: string } };
        const codesText = textAttr.text.text;
        // Parse codes - they might be in various formats
        const parsedCodes = codesText
          .split(/[\n,\s]+/)
          .map((c: string) => c.trim())
          .filter((c: string) => c.length > 0);
        setCodes(parsedCodes);
        setShowCodes(true);
        setHasLookupSecret(true);
      } else if (revealNode || regenerateNode) {
        // Has lookup_secret but codes are hidden
        setHasLookupSecret(true);
        setCodes([]);
        setShowCodes(false);
      } else {
        // No lookup_secret configured
        setHasLookupSecret(false);
        setCodes([]);
      }
    } catch (err) {
      console.error('Fetch backup codes error:', err);
      setError('Failed to load backup codes.');
    } finally {
      setIsLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    fetchBackupCodes();
  }, [fetchBackupCodes]);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    setError(null);

    try {
      const { data: flow } = await kratos.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: {
          method: 'lookup_secret',
          lookup_secret_regenerate: true,
          csrf_token: csrfToken || undefined,
        } as Parameters<typeof kratos.updateSettingsFlow>[0]['updateSettingsFlowBody'],
      });

      // Extract new codes from response
      const lookupNodes = flow.ui.nodes.filter(
        (node) => node.group === 'lookup_secret'
      );

      const codesNode = lookupNodes.find((node) => {
        return node.type === 'text' && node.attributes && 'text' in node.attributes;
      });

      if (codesNode && codesNode.attributes && 'text' in codesNode.attributes) {
        const textAttr = codesNode.attributes as { text: { text: string } };
        const codesText = textAttr.text.text;
        const parsedCodes = codesText
          .split(/[\n,\s]+/)
          .map((c: string) => c.trim())
          .filter((c: string) => c.length > 0);
        setCodes(parsedCodes);
        setShowCodes(true);
        setHasLookupSecret(true);
      }

      // Also check for codes in secrets array format
      const secretsNode = lookupNodes.find((node) => {
        const attrs = node.attributes as unknown as { secrets?: string[] };
        return attrs.secrets && Array.isArray(attrs.secrets);
      });

      if (secretsNode) {
        const attrs = secretsNode.attributes as unknown as { secrets: string[] };
        setCodes(attrs.secrets);
        setShowCodes(true);
        setHasLookupSecret(true);
      }

      onRegenerate?.();
    } catch (err) {
      console.error('Regenerate backup codes error:', err);
      setError('Failed to regenerate backup codes.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = () => {
    const content = [
      'OrbitalPassport Backup Codes',
      '=============================',
      '',
      'Keep these codes safe! Each code can only be used once.',
      'Use them to log in if you lose access to your authenticator app.',
      '',
      ...codes,
      '',
      `Generated: ${new Date().toISOString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'orbital-passport-backup-codes.txt';
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <LoadingSpinner text="Loading backup codes..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-content-primary">Backup Codes</h4>
          <p className="text-xs text-content-tertiary">
            Use these codes if you lose access to your authenticator app
          </p>
        </div>
        {hasLookupSecret && (
          <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
            Enabled
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Show codes if available */}
      {showCodes && codes.length > 0 && (
        <div className="bg-surface-secondary rounded-lg p-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm text-yellow-700 font-medium">Save these codes!</p>
                <p className="text-xs text-yellow-600">
                  Store them securely. Each code can only be used once.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {codes.map((code, index) => (
              <code
                key={index}
                className="bg-surface-primary px-3 py-2 rounded border text-sm font-mono text-center"
              >
                {code}
              </code>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCopyAll}
              className="flex-1 text-sm px-3 py-2 text-orbital-600 hover:bg-orbital-50 rounded-md transition-colors"
            >
              {copied ? 'Copied!' : 'Copy All'}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 text-sm px-3 py-2 text-orbital-600 hover:bg-orbital-50 rounded-md transition-colors"
            >
              Download
            </button>
          </div>
        </div>
      )}

      {/* Generate/Regenerate button */}
      <button
        onClick={handleRegenerate}
        disabled={isRegenerating}
        className={`w-full text-sm px-4 py-2 rounded-lg transition-colors ${
          hasLookupSecret
            ? 'border border-orange-300 text-orange-600 hover:bg-orange-50'
            : 'btn-primary'
        }`}
      >
        {isRegenerating ? (
          <LoadingSpinner size="sm" />
        ) : hasLookupSecret ? (
          'Regenerate Codes (invalidates old codes)'
        ) : (
          'Generate Backup Codes'
        )}
      </button>

      {!hasLookupSecret && (
        <p className="text-xs text-content-tertiary text-center">
          Generate backup codes to use if you lose your phone or authenticator app.
        </p>
      )}
    </div>
  );
}
